import moment from "moment";
import StaffCardPopover from "./StaffCardPopover";
import Tooltip from "../common/Tooltip";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  Card,
  CardGrid,
  CardHeader,
  CardName,
  PersonAvatar,
  FlagChip,
  ContractStatusPill,
  EmptyState,
  SkeletonBlock,
  OverflowButton,
  Button,
  PlusIcon,
  CheckIcon,
  UsersPlusIcon,
} from "./v2";

// TP-Coach-Mandate — My Agency Staff tab grid, V2 restyle (follow-up B2).
// The Staff tab has NO artboards of its own: the visual template is the
// merged Players V2 surface (PlayersGrid, boards 2a/2c/2j), mapped 1:1 the
// way the old staff grid mirrored the old player grid. PRESENTATION ONLY
// (Rule 7): every handler below is the exact same prop wiring as before —
// onStaffMenuClick (F5-gated 3-dot), kind-composite popover match,
// onChangeAgent. Deliberate staff divergences SURVIVE:
//  - the urgency pill reflects MANDATE state (active/pending/defensive/
//    none) instead of contract state — see STAFF-INVENTORY.md D1;
//  - the position element is the role + club line;
//  - NO card-body navigation (no confirmed staff-profile deep link from
//    this board);
//  - legacy lightweight / pending / external rows get NO transfer action
//    (F5 gate unchanged).

// Map a staff board row's mandate state to a pill. Branch logic and labels
// are IDENTICAL to the pre-restyle grid (STAFF-INVENTORY.md D1/C10); only
// the rendering moved onto the v2 pill family.
const mandatePill = (member) => {
  const status = (member?.mandate_status || "").toLowerCase();
  switch (status) {
    case "active":
      return {
        state: "active",
        label: member.mandate_end_date
          ? `Representation until ${moment(new Date(member.mandate_end_date)).format("MMM YYYY")}`
          : "Representation active",
      };
    case "pending":
      return { state: "pending", label: "Representation pending" };
    default:
      if (status) {
        // Defensive: the board only serves pending/active — any other
        // status falls through to a generic gray pill.
        return { state: status, label: `Representation ${status}` };
      }
      // Legacy lightweight (invitation-derived) roster member — valid, no
      // mandate. No transfer action for these rows.
      return { state: "none", label: "No representation" };
  }
};

// Staff-specific pill rendering on the 2a ContractStatusPill family
// metrics (12px/600, 4px 10px, gap 6, r999 — documented template-mapping
// decision, PARITY-FILL-STAFF.md §5):
//  - active  → green family member (bg #DCFCE7 / #15803D + leading check;
//              same saturation tier as the family's red/amber/grey);
//  - pending → ContractStatusPill "expiring" (amber + clock — the v2
//              pending treatment, cf. PendingPill);
//  - none    → ContractStatusPill "none" (white dashed);
//  - defensive other → grey family metrics WITHOUT an icon (no v2 symbol
//              carries "suspended/cancelled" semantics; calendar would lie).
const MandateStatusPill = ({ pill }) => {
  if (pill.state === "pending") {
    return <ContractStatusPill status="expiring">{pill.label}</ContractStatusPill>;
  }
  if (pill.state === "none") {
    return <ContractStatusPill status="none">{pill.label}</ContractStatusPill>;
  }
  if (pill.state === "active") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 600,
          padding: "4px 10px",
          borderRadius: 999,
          background: "#DCFCE7",
          color: "#15803D",
        }}
      >
        <CheckIcon size={11} strokeWidth={2.4} stroke="#15803D" />
        {pill.label}
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        padding: "4px 10px",
        borderRadius: 999,
        background: "#EAEFF5",
        color: "#475569",
      }}
    >
      {pill.label}
    </span>
  );
};

// Compute age from DOB, guarding against absent / impossible (future)
// dates — identical to the pre-restyle grid (STAFF-INVENTORY.md D2).
// A staff date of birth is OPTIONAL; rows created while the field still
// carried `default=date.today` are stamped with the day they were made and
// were deliberately never rewritten. An age of 0 or less means MISSING, so
// the identity line simply omits it instead of claiming "0 yrs".
const getAge = (dob) => {
  if (!dob) return null;
  const d = moment(new Date(dob));
  if (!d.isValid()) return null;
  const age = moment().diff(d, "years");
  if (age <= 0 || age > 120) return null;
  return age;
};

// Humanize a role token defensively ("sporting_director" → "Sporting
// Director") — identical function (STAFF-INVENTORY.md D5).
const humanizeRole = (member) => {
  const raw = member?.role || member?.staff_type || member?.role_type || "";
  if (!raw) return null;
  if (/[a-z]/.test(raw) && !raw.includes("_")) return raw; // already a label
  return String(raw)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Managed-by chip — Players V2 template (2a: bg #F7FAFD border #EBF0F6 r10
// 8px 12px, label 12.5/500, avatar 22 + name 13/600; 2j mobile: 10px 12px,
// label 13). Agentless rows render the players' amber "Unassigned" row so
// the card keeps its anatomy; the "Assign agent" action appears ONLY when
// the row already qualifies for the transfer action (same F5 gate as the
// 3-dot — no new capability, documented in PARITY-FILL-STAFF.md §5).
const AgentChip = ({ agent, member, mobile = false, canAssign, onAssign }) => {
  const name = agent?.name;
  if (!name) {
    return (
      <div
        className="flex items-center justify-between rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB]"
        style={{ padding: mobile ? "10px 12px" : "8px 12px" }}
      >
        <span className={`font-medium text-[#92400E] ${mobile ? "text-[13px]" : "text-[12.5px]"}`}>
          Unassigned
        </span>
        {canAssign && (
          <button
            type="button"
            onClick={() => onAssign(member)}
            className="text-[13px] font-semibold text-[#B45309] whitespace-nowrap border-0 bg-transparent p-0 cursor-pointer rounded-md hover:bg-[#FEF3C7]"
          >
            Assign agent
          </button>
        )}
      </div>
    );
  }
  const avatar = agent.avatar_url || agent.profile_picture_url;
  return (
    <div
      className="flex items-center justify-between rounded-[10px] border border-[#EBF0F6] bg-[#F7FAFD] hover:border-[#B9D8FF] hover:bg-[#F0F7FF]"
      style={{ padding: mobile ? "10px 12px" : "8px 12px" }}
    >
      <span className={`font-medium text-[#64748B] ${mobile ? "text-[13px]" : "text-[12.5px]"}`}>
        Managed by
      </span>
      <span className="flex items-center gap-[7px] min-w-0">
        <PersonAvatar src={avatar} name={name} seed={agent.account_id || name} size={22} />
        <span className="text-[13px] font-semibold text-[#0F172A] whitespace-nowrap overflow-hidden text-ellipsis">
          {name}
        </span>
      </span>
    </div>
  );
};

// 2c skeleton card anatomy (Players V2 template): header (44 circle +
// bars) → pill → meta bar → agent-chip block.
const SkeletonCard = ({ seed = 0 }) => (
  <Card gap={12} aria-hidden="true">
    <div className="flex gap-3 items-start">
      <SkeletonBlock circle height={44} tone="strong" />
      <div className="flex-1 flex flex-col gap-2 pt-1">
        <SkeletonBlock width={`${58 + (seed % 2) * 6}%`} height={13} tone="strong" />
        <SkeletonBlock width={`${36 + (seed % 2) * 4}%`} height={10} tone="light" />
      </div>
    </div>
    <SkeletonBlock width={100 + (seed % 2) * 20} height={22} radius={999} tone="chip" />
    <SkeletonBlock width={`${62 + (seed % 2) * 8}%`} height={11} tone="light" />
    <SkeletonBlock height={36} radius={10} tone="chip" />
  </Card>
);

const StaffGrid = ({
  members,
  staffLoading,
  staffError,
  showStaffPopover,
  popoverMember,
  onStaffMenuClick,
  onCloseStaffPopover,
  onChangeAgent,
  onInviteStaff,
  isCurrentUserAdmin = false,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const pad = isMobile ? "px-4" : "px-6";

  if (staffLoading) {
    return (
      <div className={pad}>
        <CardGrid marginTop={18}>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonCard key={i} seed={i} />
          ))}
        </CardGrid>
      </div>
    );
  }

  if (staffError) {
    return (
      <div className={`${pad} pt-[18px]`}>
        <div className="text-center py-12 text-red-600 bg-white border border-[#E3E9F1] rounded-xl">
          {staffError}
        </div>
      </div>
    );
  }

  if (!members || members.length === 0) {
    // Empty roster — 2c-family empty state (the staff board has no filters,
    // so empty always means an empty roster). CTA = the SAME navigate
    // dispatch as the tab-chrome "Invite staff" button.
    return (
      <div className={`${pad} pt-[18px]`}>
        <EmptyState
          icon={<UsersPlusIcon />}
          title="No staff yet"
          body="Invite coaches and staff to manage their representations alongside your players."
          bodyMaxWidth={380}
          cta={
            onInviteStaff ? (
              <Button icon={<PlusIcon />} onClick={onInviteStaff}>
                Invite staff
              </Button>
            ) : null
          }
        />
      </div>
    );
  }

  return (
    <div className={pad}>
      <CardGrid marginTop={18}>
        {members.map((member, index) => {
          // Board row ids are Staff/Coach PKs — unique only per kind, so the
          // key (and the popover match below) must include `kind`. External
          // (pre-registration) rows have id null and are keyed by ts_coach_id.
          const key = member.id
            ? `${member.kind || "member"}-${member.id}`
            : member.ts_coach_id
              ? `external-${member.ts_coach_id}`
              : member.account_id || index;
          const pill = mandatePill(member);
          // A served age of 0 or less carries the same meaning as a missing
          // one — nobody is zero years old — so it must not slip past `??`.
          const age =
            member.age > 0 ? member.age : getAge(member.date_of_birth);
          const nat = member.nationality || member.country || null;
          const role = humanizeRole(member);
          const club = member.club_name || member.current_club_name || member.club?.name || null;
          const managingAgent = member.managing_agent || null;
          // F4: board rows carry photo_url (the BE board key).
          const photo = member.photo || member.photo_url || member.profile_image || member.player_image || null;
          // F5: Change Agent ONLY for members with an ACTIVE mandate AND a
          // platform row id (Brief §4.6) — pending mandates, legacy
          // lightweight members and external (id-less) rows show their state
          // without a transfer action.
          const canTransfer =
            isCurrentUserAdmin &&
            Boolean(member.id) &&
            Boolean(member.mandate_id) &&
            member.mandate_status === "active";

          return (
            <Card
              key={key}
              gap={11}
              // Card-level hover per the 2a template; NO card/header click —
              // the staff board deliberately has no profile navigation.
              className="relative hover:border-[#B9D8FF] hover:shadow-[0_4px_14px_rgba(15,23,42,.08)]"
            >
              {showStaffPopover &&
                popoverMember &&
                popoverMember.kind === member.kind &&
                (popoverMember.id || popoverMember.account_id) === (member.id || member.account_id) && (
                  <StaffCardPopover
                    isOpen={showStaffPopover}
                    onClose={onCloseStaffPopover}
                    member={popoverMember}
                    onChangeAgent={onChangeAgent}
                  />
                )}

              {/* header: avatar · name · nationality + age (identity line
                  per 2a: flag 20×14 + "Country · NN yrs") */}
              <CardHeader
                identityGap={4}
                avatar={
                  <PersonAvatar
                    src={photo}
                    name={member.name}
                    seed={key}
                    size={44}
                  />
                }
                action={
                  canTransfer ? (
                    <OverflowButton
                      variant={isMobile ? "mobileOverflow" : "default"}
                      onClick={() => onStaffMenuClick(member)}
                      aria-label="Staff actions"
                    />
                  ) : null
                }
              >
                <CardName mobile={isMobile}>{member.name || "Unnamed staff member"}</CardName>
                {(nat?.name || age != null) && (
                  <span title={nat?.name || undefined} className="inline-flex items-center gap-1.5">
                    {nat?.image_url && (
                      <Tooltip content={nat?.name || "Nationality"} position="bottom-left">
                        <FlagChip country={nat} size="card" />
                      </Tooltip>
                    )}
                    <span className="text-[12px] text-[#64748B]">
                      {[nat?.name, age != null ? `${age} yrs` : null].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                )}
              </CardHeader>

              {/* mandate-state pill (staff divergence: mandate, not contract) */}
              <div className="flex items-center gap-2 flex-wrap">
                <MandateStatusPill pill={pill} />
              </div>

              {/* role + club line (takes the player grid's position slot) */}
              {(role || club) && (
                <div className="flex items-center gap-[7px] flex-wrap min-w-0">
                  {role && (
                    <span className="text-[13.5px] font-semibold text-[#0F172A] whitespace-nowrap">{role}</span>
                  )}
                  {club && (
                    <span className="text-[12px] text-[#64748B] whitespace-nowrap overflow-hidden text-ellipsis">{club}</span>
                  )}
                </div>
              )}

              {/* managing-agent chip (agentless → unassigned row; action
                  only where the F5 gate already allows a transfer) */}
              <AgentChip
                agent={managingAgent}
                member={member}
                mobile={isMobile}
                canAssign={canTransfer}
                onAssign={onChangeAgent}
              />
            </Card>
          );
        })}
      </CardGrid>
    </div>
  );
};

export default StaffGrid;
